import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/useToast';
import BillingService from '@/services/billingService';
import { AWBForBilling, BillingCustomer, BillingDestination, Invoice, BillingService as BillingServiceType } from '@/types/billing';
import {
    Building2,
    CheckCircle2,
    DollarSign,
    Download,
    Eye,
    FileText,
    Package,
    Plus,
    RefreshCw,
    Search,
    Send,
    Users,
    X
} from 'lucide-react';
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';

const DEFAULT_FILTERS = {
  customerId: '',
  vendorId: '',
  startDate: '',
  endDate: '',
  status: 'all',
  minAmount: '',
  maxAmount: '',
  serviceId: '',
  destinationCode: '',
  page: 1,
  limit: 50
};

const InvoiceGeneration = () => {
  const { toast } = useToast();
  const [billingType, setBillingType] = useState<'SALES' | 'PURCHASE'>('SALES');
  const [awbs, setAwbs] = useState<AWBForBilling[]>([]);
  const [selectedAwbs, setSelectedAwbs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState<Invoice | null>(null);
  
  // Enhanced filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const { customerId, vendorId, startDate: startDateFilter, endDate: endDateFilter, page, limit } = filters;

  const deferredSearchTerm = useDeferredValue(searchTerm);
  const deferredCustomerSearch = useDeferredValue(customerSearch);

  // Master data
  const [customers, setCustomers] = useState<BillingCustomer[]>([]);
  const [destinations, setDestinations] = useState<BillingDestination[]>([]);
  const [services, setServices] = useState<BillingServiceType[]>([]);

  // Load data on component mount
  const loadMasterData = useCallback(async () => {
    try {
      const [customersData, destinationsData, servicesData] = await Promise.all([
        BillingService.getCustomers(),
        BillingService.getCountries(),
        BillingService.getServices()
      ]);
      
      // Ensure we always set arrays, even if the service returns undefined
      setCustomers(Array.isArray(customersData) ? customersData : []);
      setDestinations(Array.isArray(destinationsData) ? destinationsData : []);
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (error) {
      console.error('Error loading master data:', error);
      // Set empty arrays as fallback
      setCustomers([]);
      setDestinations([]);
      setServices([]);
      toast({
        title: 'Warning',
        description: 'Some master data could not be loaded. Using defaults.',
        variant: 'destructive'
      });
    }
  }, [toast]);

  const loadAWBs = useCallback(async () => {
    try {
      setIsLoading(true);

      const params = {
        billingType,
        customerId: customerId || undefined,
        vendorId: vendorId || undefined,
        startDate: startDateFilter || undefined,
        endDate: endDateFilter || undefined,
        status: filters.status,
        serviceId: filters.serviceId || undefined,
        destinationCode: filters.destinationCode || undefined,
        minAmount: filters.minAmount || undefined,
        maxAmount: filters.maxAmount || undefined,
        page,
        limit
      };

      const response = await BillingService.getAWBsForBilling(params);
      
      if (response.success) {
        setAwbs(response.data.awbs);
      } else {
        throw new Error('Failed to load AWBs');
      }
    } catch (error) {
      toast({
        title: 'Error Loading AWBs',
        description: error instanceof Error ? error.message : 'Failed to load AWBs for billing',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    billingType,
    customerId,
    endDateFilter,
    filters.destinationCode,
    filters.maxAmount,
    filters.minAmount,
    filters.serviceId,
    filters.status,
    limit,
    page,
    startDateFilter,
    toast,
    vendorId
  ]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  useEffect(() => {
    loadAWBs();
  }, [loadAWBs]);

  // Enhanced filtering logic with real data
  const filteredAWBs = useMemo(() => {
    const search = deferredSearchTerm.trim().toLowerCase();
    const customerTerm = deferredCustomerSearch.trim().toLowerCase();
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    const minAmount = filters.minAmount ? Number(filters.minAmount) : null;
    const maxAmount = filters.maxAmount ? Number(filters.maxAmount) : null;

    return awbs.filter(awb => {
      // Search term filter (AWB number, customer name, destination)
      if (search) {
        const matchesAWB = awb.awbNumber.toLowerCase().includes(search);
        const matchesCustomer = awb.customer.name.toLowerCase().includes(search);
        const matchesVendor = awb.vendor
          ? awb.vendor.name.toLowerCase().includes(search) || awb.vendor.code.toLowerCase().includes(search)
          : false;
        const matchesDestination = awb.destination.name.toLowerCase().includes(search);
        if (!matchesAWB && !matchesCustomer && !matchesVendor && !matchesDestination) return false;
      }

      // Customer search filter
      if (customerTerm) {
        const targetName = billingType === 'PURCHASE' ? awb.vendor?.name : awb.customer.name;
        const targetCode = billingType === 'PURCHASE' ? awb.vendor?.code : awb.customer.code;
        const matchesCustomerName = targetName ? targetName.toLowerCase().includes(customerTerm) : false;
        const matchesCustomerCode = targetCode ? targetCode.toLowerCase().includes(customerTerm) : false;
        if (!matchesCustomerName && !matchesCustomerCode) return false;
      }

      // Date range filter
      if (startDate && new Date(awb.bookingDate) < startDate) return false;
      if (endDate && new Date(awb.bookingDate) > endDate) return false;

      // Status filter
      if (filters.status !== 'all' && awb.currentStatus !== filters.status) return false;

      // Service filter
      if (filters.serviceId && awb.service._id !== filters.serviceId) return false;

      // Destination filter
      if (filters.destinationCode && awb.destination.code !== filters.destinationCode) return false;

      // Amount range filter
      const billingAmount = billingType === 'SALES' ? awb.salesBilling?.finalAmount : awb.purchaseBilling?.finalAmount;
      if (minAmount !== null && billingAmount !== undefined && billingAmount < minAmount) return false;
      if (maxAmount !== null && billingAmount !== undefined && billingAmount > maxAmount) return false;

      return true;
    });
  }, [awbs, deferredSearchTerm, deferredCustomerSearch, filters, billingType]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      searchTerm.trim() ||
      customerSearch.trim() ||
      filters.customerId ||
      filters.vendorId ||
      filters.startDate ||
      filters.endDate ||
      filters.status !== 'all' ||
      filters.minAmount ||
      filters.maxAmount ||
      filters.serviceId ||
      filters.destinationCode
    );
  }, [customerSearch, filters, searchTerm]);

  // Get unique customers from AWBs for dropdown
  const uniqueCustomers = useMemo(() => {
    const customerMap = new Map<string, AWBForBilling['customer']>();
    awbs.forEach(awb => {
      if (!customerMap.has(awb.customer._id)) {
        customerMap.set(awb.customer._id, awb.customer);
      }
    });
    return Array.from(customerMap.values());
  }, [awbs]);

  type BillingVendor = NonNullable<AWBForBilling['vendor']>;

  const uniqueVendors = useMemo(() => {
    const vendorMap = new Map<string, BillingVendor>();
    awbs.forEach(awb => {
      if (awb.vendor && !vendorMap.has(awb.vendor._id)) {
        vendorMap.set(awb.vendor._id, awb.vendor);
      }
    });
    return Array.from(vendorMap.values());
  }, [awbs]);

  const customerOptions = useMemo(
    () => (customers.length > 0 ? customers : uniqueCustomers),
    [customers, uniqueCustomers]
  );

  const filteredAwbIds = useMemo(() => new Set(filteredAWBs.map(awb => awb._id)), [filteredAWBs]);
  const filteredAwbMap = useMemo(() => new Map(filteredAWBs.map(awb => [awb._id, awb])), [filteredAWBs]);

  useEffect(() => {
    setSelectedAwbs(prev => prev.filter(id => filteredAwbIds.has(id)));
  }, [filteredAwbIds]);

  const handleSelectAwb = (awbId: string, checked: boolean) => {
    if (checked) {
      setSelectedAwbs(prev => [...prev, awbId]);
    } else {
      setSelectedAwbs(prev => prev.filter(id => id !== awbId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAwbs(filteredAWBs.map(awb => awb._id));
    } else {
      setSelectedAwbs([]);
    }
  };

  const selectedTotal = useMemo(() => {
    return selectedAwbs.reduce((total, awbId) => {
      const awb = filteredAwbMap.get(awbId);
      if (!awb) return total;
      const billing = billingType === 'SALES' ? awb.salesBilling : awb.purchaseBilling;
      return total + (billing?.finalAmount || 0);
    }, 0);
  }, [selectedAwbs, filteredAwbMap, billingType]);

  const generateInvoice = async () => {
    try {
      if (selectedAwbs.length === 0) {
        toast({
          title: 'No AWBs Selected',
          description: 'Please select at least one AWB to generate invoice',
          variant: 'destructive'
        });
        return;
      }

      const selectedCount = selectedAwbs.length;
      setIsGenerating(true);

      // Get customer/vendor ID from first selected AWB
      const firstAwb = awbs.find(awb => awb._id === selectedAwbs[0]);
      if (!firstAwb) {
        throw new Error('Selected AWB not found');
      }

      const params = {
        awbIds: selectedAwbs,
        customerId: billingType === 'SALES' ? firstAwb.customer._id : undefined,
        vendorId: billingType === 'PURCHASE' ? firstAwb.vendor?._id : undefined,
        companyId: 'default-company-id', // This should come from context
        invoiceType: billingType,
        billingPeriod: {
          startDate: filters.startDate ? new Date(filters.startDate) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          endDate: filters.endDate ? new Date(filters.endDate) : new Date()
        }
      };

      const response = await BillingService.generateInvoice(params);
      
      if (response.success) {
        setGeneratedInvoice(response.data);
        setShowInvoiceDialog(true);
        setSelectedAwbs([]);
        
        toast({
          title: 'Invoice Generated Successfully!',
          description: `Invoice ${response.data.invoiceNumber} has been created with ${selectedCount} AWBs`,
        });
      } else {
        throw new Error('Failed to generate invoice');
      }
    } catch (error) {
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate invoice. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setCustomerSearch('');
    setFilters({ ...DEFAULT_FILTERS });
  };

  const handleBillingTypeChange = (value: 'SALES' | 'PURCHASE') => {
    setBillingType(value);
    setSelectedAwbs([]);
    setFilters(prev => ({
      ...prev,
      customerId: '',
      vendorId: ''
    }));
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'DELIVERED': return 'default';
      case 'IN_TRANSIT': return 'secondary';
      case 'PICKED_UP': return 'outline';
      default: return 'secondary';
    }
  };

  const hasSelection = selectedAwbs.length > 0;
  const selectAllState =
    filteredAWBs.length === 0
      ? false
      : selectedAwbs.length === filteredAWBs.length
        ? true
        : selectedAwbs.length > 0
          ? 'indeterminate'
          : false;

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-2xl font-bold tracking-tight">Invoice Generation</h3>
          <p className="text-muted-foreground">
            Select AWBs and generate invoices for billing
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {filteredAWBs.length} AWBs Available
          </Badge>
          {hasSelection && (
            <Badge variant="default" className="text-sm">
              {selectedAwbs.length} Selected
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={loadAWBs} disabled={isLoading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {isLoading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Billing Type Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Invoice Configuration
          </CardTitle>
          <CardDescription>Configure your invoice settings and billing type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6">
            <div className="space-y-2">
              <Label>Billing Type</Label>
              <Select value={billingType} onValueChange={handleBillingTypeChange}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALES">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Sales Invoice
                    </div>
                  </SelectItem>
                  <SelectItem value="PURCHASE">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Purchase Invoice
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasSelection && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 lg:ml-auto">
                <div className="text-left sm:text-right">
                  <div className="text-sm text-muted-foreground">Selected Total</div>
                  <div className="text-2xl font-bold">₹{selectedTotal.toLocaleString()}</div>
                </div>
                <Button onClick={generateInvoice} disabled={isGenerating || isLoading} size="lg">
                  {isGenerating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Generate Invoice
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search & Filter AWBs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Primary Search Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Search AWBs</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="AWB number, destination..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchTerm.trim() && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                    aria-label="Clear AWB search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{billingType === 'PURCHASE' ? 'Search Vendors' : 'Search Customers'}</Label>
              <div className="relative">
                <Users className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={billingType === 'PURCHASE' ? 'Vendor name, code...' : 'Customer name, code...'}
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="pl-10 pr-10"
                />
                {customerSearch.trim() && (
                  <button
                    type="button"
                    onClick={() => setCustomerSearch('')}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                    aria-label="Clear customer search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{billingType === 'PURCHASE' ? 'Quick Vendor Select' : 'Quick Customer Select'}</Label>
              <Select
                value={billingType === 'PURCHASE' ? filters.vendorId : filters.customerId}
                onValueChange={(value) =>
                  setFilters(prev => ({
                    ...prev,
                    customerId: billingType === 'PURCHASE' ? '' : value,
                    vendorId: billingType === 'PURCHASE' ? value : ''
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      billingType === 'PURCHASE'
                        ? 'Select vendor...'
                        : 'Select customer...'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">
                    {billingType === 'PURCHASE' ? 'All Vendors' : 'All Customers'}
                  </SelectItem>
                  {billingType === 'PURCHASE'
                    ? uniqueVendors.map(vendor => (
                      <SelectItem key={vendor._id} value={vendor._id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          <div>
                            <div className="font-medium">{vendor.name}</div>
                            <div className="text-xs text-muted-foreground">{vendor.code}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))
                    : customerOptions.map(customer => (
                      <SelectItem key={customer._id} value={customer._id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          <div>
                            <div className="font-medium">{customer.name}</div>
                            <div className="text-xs text-muted-foreground">{customer.code}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Advanced Filters */}
          <Tabs defaultValue="dates" className="w-full">
            <TabsList className="h-auto flex flex-wrap justify-start gap-1">
              <TabsTrigger value="dates">Date Range</TabsTrigger>
              <TabsTrigger value="amounts">Amount Range</TabsTrigger>
              <TabsTrigger value="status">Status Filter</TabsTrigger>
              <TabsTrigger value="routing">Service & Destination</TabsTrigger>
            </TabsList>

            <TabsContent value="dates" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="amounts" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum Amount (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.minAmount}
                    onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum Amount (₹)</Label>
                  <Input
                    type="number"
                    placeholder="No limit"
                    value={filters.maxAmount}
                    onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="status" className="space-y-4">
              <div className="space-y-2">
                <Label>AWB Status</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="w-full md:w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="DELIVERED">Delivered</SelectItem>
                    <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
                    <SelectItem value="PICKED_UP">Picked Up</SelectItem>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="routing" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service</Label>
                  <Select
                    value={filters.serviceId}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, serviceId: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All services" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Services</SelectItem>
                      {services.map(service => (
                        <SelectItem key={service._id} value={service._id}>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            <div>
                              <div className="font-medium">{service.name}</div>
                              <div className="text-xs text-muted-foreground">{service.serviceType || service.code}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination</Label>
                  <Select
                    value={filters.destinationCode}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, destinationCode: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All destinations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Destinations</SelectItem>
                      {destinations.map(destination => (
                        <SelectItem key={destination._id} value={destination.code}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            <div>
                              <div className="font-medium">{destination.name}</div>
                              <div className="text-xs text-muted-foreground">{destination.code}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Filter Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {isLoading ? 'Loading AWBs…' : `Showing ${filteredAWBs.length} of ${awbs.length} AWBs`}
            </div>
            <Button variant="outline" onClick={clearAllFilters} size="sm" disabled={!hasActiveFilters}>
              <X className="h-4 w-4 mr-2" />
              Clear All Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced AWB Selection Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Available AWBs for Billing
              </CardTitle>
              <CardDescription>
                Select AWBs to include in the invoice
              </CardDescription>
            </div>
            {filteredAWBs.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={selectAllState}
                  onCheckedChange={(checked) => handleSelectAll(checked === true)}
                  disabled={isLoading}
                />
                <Label htmlFor="select-all" className="text-sm font-medium">
                  Select All ({filteredAWBs.length})
                </Label>
                {hasSelection && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedAwbs([])}>
                    <X className="h-4 w-4 mr-1" />
                    Clear Selection
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredAWBs.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">
                {isLoading ? 'Loading AWBs...' : 'No AWBs Found'}
              </h3>
              <p className="text-muted-foreground">
                {isLoading ? 'Please wait while we fetch the AWB data' : 'Try adjusting your search criteria or filters'}
              </p>
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table className="min-w-[980px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Select</TableHead>
                    <TableHead>AWB Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden md:table-cell">Service</TableHead>
                    <TableHead className="hidden md:table-cell">Destination</TableHead>
                    <TableHead className="hidden lg:table-cell">Weight (KG)</TableHead>
                    <TableHead className="hidden lg:table-cell">Pieces</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={`skeleton-${i}`} className="animate-pulse">
                        <TableCell>
                          <div className="h-4 w-4 rounded bg-muted" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-28 rounded bg-muted" />
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            <div className="h-4 w-40 rounded bg-muted" />
                            <div className="h-3 w-20 rounded bg-muted" />
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="h-5 w-24 rounded bg-muted" />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="h-4 w-32 rounded bg-muted" />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="h-4 w-12 rounded bg-muted" />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="h-4 w-10 rounded bg-muted" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-20 rounded bg-muted" />
                        </TableCell>
                        <TableCell>
                          <div className="h-5 w-24 rounded bg-muted" />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="h-4 w-20 rounded bg-muted" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    filteredAWBs.map((awb) => (
                      <TableRow
                        key={awb._id}
                        className={selectedAwbs.includes(awb._id) ? 'bg-muted/50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedAwbs.includes(awb._id)}
                            onCheckedChange={(checked) => handleSelectAwb(awb._id, checked === true)}
                            disabled={isLoading}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{awb.awbNumber}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{awb.customer.name}</div>
                            <div className="text-xs text-muted-foreground">{awb.customer.code}</div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant="outline">{awb.service.serviceType}</Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{awb.destination.name}</TableCell>
                        <TableCell className="hidden lg:table-cell">{awb.totalChargeableWeight}</TableCell>
                        <TableCell className="hidden lg:table-cell">{awb.totalPieces}</TableCell>
                        <TableCell className="font-medium">
                          ₹{((billingType === 'SALES' ? awb.salesBilling : awb.purchaseBilling)?.finalAmount || 0).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(awb.currentStatus)}>
                            {awb.currentStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{new Date(awb.bookingDate).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Preview Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Invoice Generated Successfully
            </DialogTitle>
            <DialogDescription>
              Your invoice has been created and is ready for review
            </DialogDescription>
          </DialogHeader>
          
          {generatedInvoice && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Invoice Number</Label>
                  <div className="font-semibold">{generatedInvoice.invoiceNumber}</div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Type</Label>
                  <div>
                    <Badge variant="default">{generatedInvoice.invoiceType}</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Total Amount</Label>
                  <div className="text-2xl font-bold">₹{generatedInvoice.totalAmount.toLocaleString()}</div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Due Date</Label>
                  <div>{generatedInvoice.dueDate.toLocaleDateString()}</div>
                </div>
              </div>

              <Separator />

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Invoice
                </Button>
                <Button variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button className="flex-1">
                  <Send className="h-4 w-4 mr-2" />
                  Send Invoice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceGeneration;
